import { Box, Flex, useMediaQuery } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import useCheckChannelExportability, {
  CheckChannelExportabilityResponse,
} from 'pages/Conversations/hooks/useCheckChannelExportability';
import {
  closeChannelOverlay,
  ExportType,
  openChannelOverlay,
  toggleConversationDetailsModal,
  updateExportData,
} from 'pages/Conversations/state/conversationsSlice';
import { getChannelParticipants } from 'pages/Conversations/utils';
import React, { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import {
  AvatarGroupSelect,
  IAvatarGroupSelectOption,
  useAvatarGroupSelectOptions,
} from 'shared/components/Avatars/AvatarGroupSelect';
import ChannelStatusSelect from 'shared/components/ChannelStatusSelect/ChannelStatusSelect';
import { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { Placement } from 'shared/enums/Placement';
import { useIsOverflow } from 'shared/hooks/useIsOverflow';
import { usePetParentsWithDisplayName } from 'shared/hooks/usePetParentsWithDisplayName';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { useSummaryProvider } from 'shared/providers/SummaryProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  ChannelStatusAction,
  ChannelViewChannelFragment,
  ChannelViewChannelMemberFragment,
  ChannelViewClientDropdownClinicPetParentFragment,
  ClinicPetParent,
  useGetChannelViewClientDropdownPetParentsLazyQuery,
  useUpdateChannelAssigneesMutation,
} from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import ConversationDetailsModal from './ConversationDetailsModal/components/ConversationDetailsModal';
import { PetParentSelector } from './PetParentSelector';
import SummaryButtonHiddenPopover from './SummaryButtonHiddenPopover';

interface IProps {
  channel?: ChannelViewChannelFragment;
  isOverlay?: boolean;
}

export enum ExportConversationOptions {
  ExportToPDF = 'ExportPDF',
  ExportToDB = 'ExportDB',
  IncludeNotes = 'IncludeNotes',
}

const ChannelViewHeader = ({ channel, isOverlay }: IProps): JSX.Element => {
  const scrollContainerRef = useRef(null);
  const history = useHistory();
  const [channelExportability, setChannelExportability] = useState<CheckChannelExportabilityResponse>();
  const { fetchSummary } = useSummaryProvider();
  const dispatch = useAppDispatch();

  const { isDetailsModalOpen } = useAppSelector((state) => state.conversations.channelViewData);
  const { closeSideBarPanel } = useSidePanelSearch();
  const { checkChannelExportability } = useCheckChannelExportability();
  const { primaryIntegrationName } = useIntegrationsProvider();
  const [isSmallerThanMobileMaxWidth] = useMediaQuery('(' + DeviceSizes.MobileMaxWidth + ')');
  const isOverflow = useIsOverflow({ ref: scrollContainerRef, overflowDirection: 'x' });
  const { trackEvent } = useTracking();
  const { isMobile } = useResolutionProvider();
  const { channelPetParents, channelPetParentIds, allChannelPets, petParentChannelMembers } = getChannelParticipants(
    channel?.channelMembers,
  );
  const { petParentsWithDisplayName } = usePetParentsWithDisplayName({ petParents: channelPetParents });

  const getPetParentOptions = useCallback(
    (
      parents: (ChannelViewClientDropdownClinicPetParentFragment & { displayName?: string })[],
      addSelection?: (parent: ChannelViewClientDropdownClinicPetParentFragment) => boolean,
    ): IAvatarGroupSelectOption[] => {
      if (!parents || !parents?.length) return [];

      if (addSelection) {
        return parents.map((parent) => {
          return {
            text: parent.displayName ?? [parent.firstName, parent.lastName].join(' ').trim(),
            value: { ...parent, clinicPetParentId: parent.id },
            isSelected: addSelection(parent),
            clinicPetParent: parent,
          };
        });
      }
      return parents.map((parent) => {
        return { text: [parent.firstName, parent.lastName].join(' ').trim(), value: parent, clinicPetParent: parent };
      });
    },
    [],
  );

  /**
   * Pet parent channel members that are active
   */
  const petParentAvatars: IAvatarGroupSelectOption[] = useMemo(() => {
    return petParentsWithDisplayName.map((petParent) => {
      const { id, firstName, lastName } = petParent;
      return {
        text: petParent.displayName,
        value: { clinicPetParent: petParent, id, firstName, lastName },
        clinicPetParent: petParent,
        isSelected: true,
      };
    });
  }, [petParentsWithDisplayName]);

  const [getAvailablePetParentList, { data: availablePetParentData }] =
    useGetChannelViewClientDropdownPetParentsLazyQuery({
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
      notifyOnNetworkStatusChange: true,
    });

  const { petParentsWithDisplayName: availablePetParentsWithDisplayName } = usePetParentsWithDisplayName({
    petParents: availablePetParentData?.findManyClinicPetParent || [],
  });
  const availablePetParentDisplayNames = useMemo(
    () => new Map(availablePetParentsWithDisplayName.map((petParent) => [petParent.id, petParent.displayName])),
    [availablePetParentsWithDisplayName],
  );

  const petParentOptions = useMemo(() => {
    const checkPetParentAvailable = (parent: ChannelViewClientDropdownClinicPetParentFragment): boolean => {
      if (channel && parent) {
        const member = parent.channelMembers?.find((member) => member.channel?.id === channel.id);
        return !member?.removedAt && petParentAvatars.some((a) => a.value?.id === parent.id);
      }
      return false;
    };

    const petParentOptions = getPetParentOptions(
      availablePetParentData?.findManyClinicPetParent.map((cpp) => ({
        ...cpp,
        displayName: availablePetParentDisplayNames.get(cpp.id),
      })) || [],
      checkPetParentAvailable,
    );

    const petParentsInChannelWithoutPetAssignment = petParentAvatars.filter((f) => {
      return petParentOptions.every((ppo) => ppo.value.id !== f.value.id);
    });

    return petParentOptions.concat(petParentsInChannelWithoutPetAssignment);
  }, [channel, petParentAvatars, availablePetParentData, availablePetParentDisplayNames, getPetParentOptions]);

  const handleLoadClientDropdownOptions = useCallback(async () => {
    const conversationPetIds = allChannelPets.map((pet) => pet.id);
    if (conversationPetIds) {
      getAvailablePetParentList({ variables: { petIds: conversationPetIds } });
    }
  }, [allChannelPets, getAvailablePetParentList]);

  const onCloseDetailsModal = useCallback(() => {
    dispatch(toggleConversationDetailsModal({ isOpen: false }));
    closeSideBarPanel();
  }, [closeSideBarPanel, dispatch]);

  const [updateChannelAssignees, { loading: isUpdateChannelLoading }] = useUpdateChannelAssigneesMutation();

  const assignees = useMemo(() => channel?.assignees || [], [channel]);

  const assigneeOptions = useAvatarGroupSelectOptions({
    selectedUserIds: assignees.map(({ id }) => id),
  });

  const handleAssigneeToggle = useCallback(
    (e: MouseEvent, option: IOption) => {
      if (!channel || isUpdateChannelLoading) return;
      let assigneeUserIds = [];
      if (!option.isSelected) {
        assigneeUserIds = assignees.concat(option.value).map(({ id }) => ({ id }));
        trackEvent(ConversationEvents.ChannelAddNewAssignee, { source: 'Channel View - Header' });
      } else {
        assigneeUserIds = assignees.filter((a) => a.id !== option.value.id).map(({ id }) => ({ id }));
      }

      updateChannelAssignees({
        variables: {
          where: {
            id: channel.id,
          },
          data: { twilioChannelSid: channel.twilioChannelSid, assignees: { set: assigneeUserIds } },
        },
      });
    },
    [channel, isUpdateChannelLoading, updateChannelAssignees, assignees, trackEvent],
  );

  // Disable pet parent from being selected if they are in another active convo and have not been removed from that convo
  const setDisabled = useCallback(
    (parent: ClinicPetParent) => {
      return !!parent.channelMembers?.filter(
        (member) =>
          member?.channel?.channelStatus?.channelStatusAction === ChannelStatusAction.Active &&
          !member?.removedAt &&
          !petParentChannelMembers.some(
            (a: ChannelViewChannelMemberFragment) => a?.clinicPetParent?.id === parent.id && !a?.removedAt,
          ),
      )?.length;
    },
    [petParentChannelMembers],
  );

  const openExportModal = useCallback(
    (exportType: ExportType) => {
      dispatch(
        updateExportData({
          isExportModalOpen: true,
          selectedExportType: exportType,
          channelPetParents,
          channelId: channel?.id,
        }),
      );
    },
    [dispatch, channelPetParents, channel?.id],
  );

  const fetchMenuData = useCallback(async () => {
    const exportability = await checkChannelExportability(channel?.id || '');
    setChannelExportability(exportability);
  }, [checkChannelExportability, channel?.id]);

  const menuItems: MenuItemProps[] = useMemo(() => {
    return [
      {
        label: 'Summarize Conversation',
        value: 'SummarizeConversation',
        leftElement: <Icon name="magic" />,
        onSelect: (): void => {
          if (fetchSummary) {
            fetchSummary();
          }
        },
      },
      {
        label: (
          <Tooltip
            isDisabled={!channelExportability}
            label={channelExportability?.message || `Client and/or patient are not in ${primaryIntegrationName}`}
          >
            <Box position="relative" data-testid="dropdown-option-2">
              <Text size="sm">Export to {primaryIntegrationName}</Text>
            </Box>
          </Tooltip>
        ),
        isDisabled: !!channelExportability,
        value: 'ExportToPims',
        leftElement: <Icon name="download" />,
        onSelect: (): void => {
          openExportModal('ExportToPims');
        },
      },
      {
        label: 'Download PDF',
        value: 'DownloadPdf',
        leftElement: <Icon name="form" />,
        onSelect: (): void => {
          openExportModal('DownloadPdf');
        },
      },
    ];
  }, [channelExportability, openExportModal, primaryIntegrationName, fetchSummary]);

  const handleOpenChannelOverlayClick = useCallback((): void => {
    if (channel?.id) {
      dispatch(openChannelOverlay({ channelId: channel.id }));
    }
  }, [channel?.id, dispatch]);

  const onCloseButtonClick = (): void => {
    if (isOverlay) {
      dispatch(closeChannelOverlay());
    } else {
      history.push(RouteBasePaths.Conversations);
    }
  };

  const useCompactView = useMemo(
    () => isSmallerThanMobileMaxWidth || isOverlay,
    [isOverlay, isSmallerThanMobileMaxWidth],
  );

  return (
    <Flex
      h="min-content"
      className="ChannelView__Header"
      borderBottomWidth="1px"
      data-testid="channel-view-toolbar-container"
    >
      <Flex
        w="100%"
        align="center"
        justify="space-between"
        overflowX="auto"
        ref={scrollContainerRef}
        py={2}
        pl={2}
        {...(useCompactView && { gap: 4 })}
      >
        <PetParentSelector petParentAvatars={petParentAvatars} isOverlay={isOverlay} />

        {/* Right Elements */}
        <Flex gap={2}>
          {/* Channel Status */}
          {!!channel?.channelStatus && (
            <Flex gap={2} align="center" ml={2}>
              <ChannelStatusSelect
                currentStatus={{
                  name: channel?.channelStatus?.name,
                  color: channel?.channelStatus?.color,
                  id: channel?.channelStatus?.id,
                }}
                channelId={channel.id}
                placement={Placement.BottomEnd}
                showRightIcon={false}
              />
            </Flex>
          )}

          {/* Assignees */}
          <Flex gap={2} align="center">
            <AvatarGroupSelect
              options={assigneeOptions}
              popoverPlacement={PopoverPlacement.BottomEnd}
              addButtonTooltip="Assign team member"
              onToggle={handleAssigneeToggle}
              showRemoveButton={true}
              max={1}
            />
          </Flex>

          {!isOverlay && !isMobile && (
            <Tooltip label="Open as pop-up">
              <Button iconName="popup" variant="ghostNeutral" onClick={handleOpenChannelOverlayClick} />
            </Tooltip>
          )}

          {/* Actions */}
          <SummaryButtonHiddenPopover
            anchor={
              <Box data-testid="convo-export-dropdown-button" {...(isOverflow && { pr: 4 })}>
                <Menu
                  onOpen={fetchMenuData}
                  buttonProps={{
                    variant: 'ghostNeutral',
                    size: 'md',
                    leftIconName: 'dotsVertical',
                    showRightIcon: false,
                  }}
                  isLazy
                  containerProps={{
                    placement: 'left-start',
                  }}
                  listProps={{
                    menuItems: menuItems,
                  }}
                />
              </Box>
            }
          />
        </Flex>
      </Flex>
      <Flex
        align="center"
        pr={2}
        pl={1}
        pos="sticky"
        right="0"
        bgColor="background.default"
        borderTopRadius="md"
        {...(isOverflow && {
          boxShadow: '0px 0px 15px -3px rgba(0, 0, 0, 0.10), 0px 0px 6px -2px rgba(0, 0, 0, 0.05)',
          borderLeftWidth: '1px',
          pr: 1,
        })}
      >
        <Button data-testid="convo-close-button" iconName="close" variant="ghostNeutral" onClick={onCloseButtonClick} />
      </Flex>
      <ConversationDetailsModal
        clinicPetParentIds={channelPetParentIds}
        channelPetParents={channelPetParents}
        petParentOptions={petParentOptions}
        handleLoadClientDropdownOptions={handleLoadClientDropdownOptions}
        isPetParentInAnotherConversation={setDisabled}
        channel={channel}
        isOpen={isDetailsModalOpen}
        onClose={onCloseDetailsModal}
      />
    </Flex>
  );
};

export default ChannelViewHeader;
